import React, { useState, useEffect, useCallback } from 'react';

const CS = { USD: '$', SGD: 'S$', EUR: '€', GBP: '£', INR: '₹', PHP: '₱', IDR: 'Rp' };
const FX_FALLBACK = { USD: 1, SGD: 1.2768, EUR: 0.86723, GBP: 0.74954, INR: 92.37, PHP: 59.559, IDR: 16958 };

function fmt(usd, cur, rates) {
  const n = usd * (rates[cur] || 1), s = CS[cur] || '$';
  if (Math.abs(n) >= 1e12) return s + (n / 1e12).toFixed(1) + 'T';
  if (Math.abs(n) >= 1e9) return s + (n / 1e9).toFixed(1) + 'B';
  if (Math.abs(n) >= 1e6) return s + (n / 1e6).toFixed(1) + 'M';
  if (Math.abs(n) >= 1e3) return s + (n / 1e3).toFixed(1) + 'K';
  return s + Math.round(n).toLocaleString();
}
function fN(n) { if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M'; if (n >= 1e3) return (n / 1e3).toFixed(0) + 'K'; return Math.round(n) + ''; }
function pct(n) { return Math.round(n) + '%'; }
function npvCalc(cfs, r) { return cfs.reduce((s, c, t) => s + c / Math.pow(1 + r, t + 1), 0); }
function irrCalc(cfs) { let r = .1; for (let i = 0; i < 200; i++) { let f = 0, df = 0; cfs.forEach((c, t) => { f += c / Math.pow(1 + r, t); df -= t * c / Math.pow(1 + r, t + 1); }); if (Math.abs(df) < 1e-12) break; let nr = r - f / df; if (Math.abs(nr - r) < 1e-7) break; r = nr; } return isFinite(r) && r > -1 ? r : null; }
function alertCalc(alertsMo, fp, inv, tFP, tInv, hr) {
  const N = alertsMo * 12, cH = N * inv / 60, tp = N * (1 - fp / 100), ctx = tp / (1 - tFP / 100), ctxH = ctx * tInv / 60;
  return { cH, ctxH, sav: Math.max(0, (cH - ctxH) * hr), red: N > 0 ? ((N - ctx) / N * 100) : 0 };
}

const TABS = [
  {
    id: 'fincrime', name: 'Financial Crime', icon: '🛡', color: '#1E3A5F', desc: 'Retail AML · Trade AML · Market Surveillance',
    subs: [{ id: 'retail', label: 'Retail AML', on: true }, { id: 'trade', label: 'Trade AML', on: true }, { id: 'market', label: 'Market Surveillance', on: false }],
    constants: [
      { id: 'c_rtfp', label: 'Retail target FP', dflt: 15, unit: '%' }, { id: 'c_rtinv', label: 'Retail target inv', dflt: 12, unit: 'min' },
      { id: 'c_ttfp', label: 'Trade target FP', dflt: 20, unit: '%' }, { id: 'c_ttinv', label: 'Trade target inv', dflt: 25, unit: 'min' },
      { id: 'c_mtfp', label: 'Market target FP', dflt: 18, unit: '%' }, { id: 'c_mtinv', label: 'Market target inv', dflt: 8, unit: 'min' },
      { id: 'c_strred', label: 'STR reduction', dflt: 85, unit: '%' }, { id: 'c_regred', label: 'Reg risk reduction', dflt: 75, unit: '%' },
    ],
    inputs: [
      { id: 'retail_alerts', label: 'Retail alerts/mo', min: 500, max: 50000, step: 500, dflt: 5000, unit: '', sub: 'retail' },
      { id: 'retail_fp', label: 'Retail FP rate', min: 20, max: 99, step: 1, dflt: 90, unit: '%', sub: 'retail' },
      { id: 'retail_inv', label: 'Retail inv time', min: 10, max: 120, step: 1, dflt: 45, unit: ' min', sub: 'retail' },
      { id: 'trade_alerts', label: 'Trade alerts/mo', min: 100, max: 20000, step: 100, dflt: 1500, unit: '', sub: 'trade' },
      { id: 'trade_fp', label: 'Trade FP rate', min: 20, max: 99, step: 1, dflt: 92, unit: '%', sub: 'trade' },
      { id: 'trade_inv', label: 'Trade inv time', min: 20, max: 180, step: 5, dflt: 90, unit: ' min', sub: 'trade' },
      { id: 'market_alerts', label: 'Market alerts/mo', min: 100, max: 30000, step: 100, dflt: 2000, unit: '', sub: 'market' },
      { id: 'market_fp', label: 'Market FP rate', min: 20, max: 99, step: 1, dflt: 88, unit: '%', sub: 'market' },
      { id: 'market_inv', label: 'Market inv time', min: 10, max: 60, step: 1, dflt: 20, unit: ' min', sub: 'market' },
      { id: 'strs', label: 'STRs/month', min: 5, max: 500, step: 5, dflt: 30, unit: '', sub: '_shared' },
      { id: 'str_time', label: 'STR prep time', min: 1, max: 10, step: 0.5, dflt: 3, unit: ' hrs', sub: '_shared' },
      { id: 'fte_cost', label: 'FTE cost (annual)', min: 10, max: 300, step: 5, dflt: 80, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_shared' },
      { id: 'reg_fine', label: 'Reg fine risk', min: 0, max: 1000, step: 5, dflt: 50, unit: 'M', mult: 1e6, fmtType: 'cur', sub: '_shared' },
      { id: 'license', label: 'License (annual)', min: 100, max: 10000, step: 50, dflt: 1200, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_ctx' },
      { id: 'impl', label: 'Implementation', min: 50, max: 5000, step: 50, dflt: 600, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_ctx' },
    ],
    calc: (v, subs, c) => {
      const hr = v.fte_cost / 2080; const rows = []; const bars = [];
      if (subs.retail) { const r = alertCalc(v.retail_alerts, v.retail_fp, v.retail_inv, c.c_rtfp, c.c_rtinv, hr); rows.push({ n: 'Retail AML', s: `FP ${v.retail_fp}%→${c.c_rtfp}% · ${v.retail_inv}→${c.c_rtinv}min`, curr: r.cH * hr, after: r.ctxH * hr, sav: r.sav }); bars.push({ l: 'Retail FP', b: v.retail_fp, a: c.c_rtfp, u: '%', c: '#dc2626', mx: 100 }, { l: 'Retail inv', b: v.retail_inv, a: c.c_rtinv, u: 'm', c: '#d97706', mx: 120 }); }
      if (subs.trade) { const r = alertCalc(v.trade_alerts, v.trade_fp, v.trade_inv, c.c_ttfp, c.c_ttinv, hr); rows.push({ n: 'Trade AML', s: `FP ${v.trade_fp}%→${c.c_ttfp}% · ${v.trade_inv}→${c.c_ttinv}min`, curr: r.cH * hr, after: r.ctxH * hr, sav: r.sav }); bars.push({ l: 'Trade FP', b: v.trade_fp, a: c.c_ttfp, u: '%', c: '#7c3aed', mx: 100 }); }
      if (subs.market) { const r = alertCalc(v.market_alerts, v.market_fp, v.market_inv, c.c_mtfp, c.c_mtinv, hr); rows.push({ n: 'Market Surveillance', s: `FP ${v.market_fp}%→${c.c_mtfp}%`, curr: r.cH * hr, after: r.ctxH * hr, sav: r.sav }); }
      const strSav = v.strs * 12 * v.str_time * (c.c_strred / 100) * hr; rows.push({ n: 'STR Automation', s: `${c.c_strred}% reduction`, curr: v.strs * 12 * v.str_time * hr, after: v.strs * 12 * v.str_time * (1 - c.c_strred / 100) * hr, sav: strSav });
      const regSav = v.reg_fine * (c.c_regred / 100); rows.push({ n: 'Reg fine avoidance', s: `${c.c_regred}% risk reduction`, curr: v.reg_fine, after: v.reg_fine * (1 - c.c_regred / 100), sav: regSav });
      const gross = rows.reduce((s, r) => s + r.sav, 0);
      return { rows, bars, gross, y1cost: v.license + v.impl, assume: `Retail FP ${c.c_rtfp}%, Trade FP ${c.c_ttfp}%, STR ${c.c_strred}%, Reg ${c.c_regred}%. FATF, MAS benchmarks.` };
    }
  },
  {
    id: 'fraud', name: 'Fraud', icon: '🔒', color: '#DC2626', desc: 'Mule Networks · Payment Fraud',
    subs: [{ id: 'mule', label: 'Mule Detection', on: true }, { id: 'payment', label: 'Payment Fraud', on: true }],
    constants: [
      { id: 'c_mblock', label: 'Mule block rate', dflt: 65, unit: '%' }, { id: 'c_mred', label: 'Mule alert reduction', dflt: 65, unit: '%' },
      { id: 'c_mtinv', label: 'Mule target inv', dflt: 15, unit: 'min' }, { id: 'c_pprev', label: 'Payment prevent', dflt: 55, unit: '%' },
      { id: 'c_ptfp', label: 'Payment target FP', dflt: 20, unit: '%' }, { id: 'c_ptinv', label: 'Payment target inv', dflt: 8, unit: 'min' },
      { id: 'c_cbpp', label: 'CB uplift', dflt: 30, unit: 'pp' }, { id: 'c_cbcap', label: 'CB cap', dflt: 60, unit: '%' },
    ],
    inputs: [
      { id: 'mule_alerts', label: 'Mule alerts/mo', min: 100, max: 20000, step: 100, dflt: 2000, unit: '', sub: 'mule' },
      { id: 'mule_losses', label: 'Mule losses/yr', min: 0.5, max: 200, step: 0.5, dflt: 10, unit: 'M', mult: 1e6, fmtType: 'cur', sub: 'mule' },
      { id: 'mule_inv', label: 'Mule inv time', min: 10, max: 120, step: 5, dflt: 60, unit: ' min', sub: 'mule' },
      { id: 'pmt_alerts', label: 'Payment alerts/mo', min: 500, max: 100000, step: 500, dflt: 8000, unit: '', sub: 'payment' },
      { id: 'pmt_losses', label: 'Payment losses/yr', min: 0.5, max: 500, step: 0.5, dflt: 25, unit: 'M', mult: 1e6, fmtType: 'cur', sub: 'payment' },
      { id: 'pmt_fp', label: 'Payment FP rate', min: 30, max: 98, step: 1, dflt: 85, unit: '%', sub: 'payment' },
      { id: 'pmt_inv', label: 'Payment inv time', min: 5, max: 60, step: 1, dflt: 15, unit: ' min', sub: 'payment' },
      { id: 'cb_rate', label: 'CB recovery rate', min: 5, max: 60, step: 1, dflt: 15, unit: '%', sub: 'payment' },
      { id: 'fte_cost', label: 'FTE cost', min: 10, max: 300, step: 5, dflt: 75, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_shared' },
      { id: 'license', label: 'License', min: 100, max: 10000, step: 50, dflt: 1000, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_ctx' },
      { id: 'impl', label: 'Implementation', min: 50, max: 5000, step: 50, dflt: 500, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_ctx' },
    ],
    calc: (v, subs, c) => {
      const hr = v.fte_cost / 2080; const rows = []; const bars = [];
      if (subs.mule) {
        const bl = v.mule_losses * (c.c_mblock / 100); const mA = v.mule_alerts * 12, cH = mA * v.mule_inv / 60, ctxH = mA * (1 - c.c_mred / 100) * c.c_mtinv / 60, ops = Math.max(0, (cH - ctxH) * hr);
        rows.push({ n: 'Mule blocked', s: `${c.c_mblock}%`, curr: v.mule_losses, after: v.mule_losses * (1 - c.c_mblock / 100), sav: bl });
        rows.push({ n: 'Mule ops', s: `${c.c_mred}% alert cut · ${v.mule_inv}→${c.c_mtinv}min`, curr: cH * hr, after: ctxH * hr, sav: ops });
        bars.push({ l: 'Mule inv', b: v.mule_inv, a: c.c_mtinv, u: 'm', c: '#d97706', mx: 120 });
      }
      if (subs.payment) {
        const prev = v.pmt_losses * (c.c_pprev / 100); const rem = v.pmt_losses - prev;
        const currCb = rem * (v.cb_rate / 100), newCb = rem * (Math.min(v.cb_rate + c.c_cbpp, c.c_cbcap) / 100), cb = newCb - currCb;
        const r = alertCalc(v.pmt_alerts, v.pmt_fp, v.pmt_inv, c.c_ptfp, c.c_ptinv, hr);
        rows.push({ n: 'Payment prevented', s: `${c.c_pprev}%`, curr: v.pmt_losses, after: v.pmt_losses * (1 - c.c_pprev / 100), sav: prev });
        rows.push({ n: 'CB uplift', s: `${v.cb_rate}%→${Math.min(v.cb_rate + c.c_cbpp, c.c_cbcap)}%`, curr: currCb, after: newCb, sav: cb });
        rows.push({ n: 'Payment ops', s: `FP ${v.pmt_fp}%→${c.c_ptfp}%`, curr: r.cH * hr, after: r.ctxH * hr, sav: r.sav });
        bars.push({ l: 'Pmt FP', b: v.pmt_fp, a: c.c_ptfp, u: '%', c: '#7c3aed', mx: 100 });
      }
      const gross = rows.reduce((s, r) => s + r.sav, 0);
      return { rows, bars, gross, y1cost: v.license + v.impl, assume: `Mule ${c.c_mblock}% block. Payment ${c.c_pprev}%. CB +${c.c_cbpp}pp. Feedzai benchmarks.` };
    }
  },
  {
    id: 'custintel', name: 'Customer Intel', icon: '👥', color: '#2563EB', desc: 'Cross-sell · Upsell · Churn · Segmentation',
    subs: [{ id: 'xsell', label: 'Cross-sell', on: true }, { id: 'upsell', label: 'Upsell', on: true }, { id: 'retention', label: 'Churn Prevention', on: true }, { id: 'segmentation', label: 'Micro-segmentation', on: false }],
    constants: [{ id: 'c_xup', label: 'Cross-sell uplift', dflt: 40, unit: '%' }, { id: 'c_uup', label: 'Upsell uplift', dflt: 35, unit: '%' }, { id: 'c_cred', label: 'Churn reduction', dflt: 25, unit: '%' }, { id: 'c_ceff', label: 'Campaign efficiency', dflt: 30, unit: '%' }],
    inputs: [
      { id: 'customers', label: 'Customers', min: 10, max: 10000, step: 10, dflt: 500, unit: 'K', mult: 1e3, fmtType: 'cnt', sub: '_shared' },
      { id: 'arpc', label: 'Revenue/cust/yr', min: 50, max: 5000, step: 50, dflt: 500, unit: '', fmtType: 'cur_raw', sub: '_shared' },
      { id: 'xsell_rate', label: 'Cross-sell rate', min: 1, max: 30, step: 1, dflt: 8, unit: '%', sub: 'xsell' },
      { id: 'xsell_margin', label: 'X-sell margin', min: 5, max: 80, step: 5, dflt: 35, unit: '%', sub: 'xsell' },
      { id: 'upsell_rate', label: 'Upsell rate', min: 1, max: 25, step: 1, dflt: 5, unit: '%', sub: 'upsell' },
      { id: 'upsell_rev', label: 'Upsell uplift', min: 5, max: 50, step: 5, dflt: 20, unit: '%', sub: 'upsell' },
      { id: 'churn_rate', label: 'Churn rate', min: 1, max: 30, step: 1, dflt: 12, unit: '%', sub: 'retention' },
      { id: 'segment_campaigns', label: 'Campaign spend', min: 100, max: 20000, step: 100, dflt: 2000, unit: 'K', mult: 1e3, fmtType: 'cur', sub: 'segmentation' },
      { id: 'license', label: 'License', min: 100, max: 5000, step: 50, dflt: 800, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_ctx' },
      { id: 'impl', label: 'Implementation', min: 50, max: 3000, step: 50, dflt: 350, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_ctx' },
    ],
    calc: (v, subs, c) => {
      const cu = v.customers; const rows = []; const bars = [];
      if (subs.xsell) { const n = cu * (v.xsell_rate / 100) * (c.c_xup / 100), rev = n * v.arpc * (v.xsell_margin / 100); rows.push({ n: 'Cross-sell', s: `+${c.c_xup}%: ${fN(n)} new`, curr: 0, after: 0, sav: rev }); bars.push({ l: 'X-sell', b: v.xsell_rate, a: +(v.xsell_rate * (1 + c.c_xup / 100)).toFixed(1), u: '%', c: '#059669', mx: 30 }); }
      if (subs.upsell) { const n = cu * (v.upsell_rate / 100) * (c.c_uup / 100), rev = n * v.arpc * (v.upsell_rev / 100); rows.push({ n: 'Upsell', s: `+${c.c_uup}%`, curr: 0, after: 0, sav: rev }); }
      if (subs.retention) { const s = cu * (v.churn_rate / 100) * (c.c_cred / 100), rev = s * v.arpc; rows.push({ n: 'Churn prevention', s: `${c.c_cred}% reduction`, curr: cu * (v.churn_rate / 100) * v.arpc, after: (cu * (v.churn_rate / 100) - s) * v.arpc, sav: rev }); bars.push({ l: 'Churn', b: v.churn_rate, a: +(v.churn_rate * (1 - c.c_cred / 100)).toFixed(1), u: '%', c: '#dc2626', mx: 30 }); }
      if (subs.segmentation) { const s = v.segment_campaigns * (c.c_ceff / 100); rows.push({ n: 'Campaign efficiency', s: `${c.c_ceff}%`, curr: v.segment_campaigns, after: v.segment_campaigns * (1 - c.c_ceff / 100), sav: s }); }
      const gross = rows.reduce((s, r) => s + r.sav, 0);
      return { rows, bars, gross, y1cost: v.license + v.impl, assume: `X-sell +${c.c_xup}%, Upsell +${c.c_uup}%, Churn ${c.c_cred}%, Campaign ${c.c_ceff}%. McKinsey, Bain.` };
    }
  },
  {
    id: 'supply', name: 'Supply Chain', icon: '🔗', color: '#059669', desc: 'Disruption · Vendor · Sanctions · WC',
    subs: [{ id: 'disruption', label: 'Disruption', on: true }, { id: 'vendor', label: 'Vendor', on: true }, { id: 'compliance', label: 'Sanctions', on: true }, { id: 'workcap', label: 'Working Capital', on: true }],
    constants: [{ id: 'c_dred', label: 'Disruption reduction', dflt: 45, unit: '%' }, { id: 'c_pbuf', label: 'Procurement buffer', dflt: 0.3, unit: '%' }, { id: 'c_sred', label: 'Screening reduction', dflt: 60, unit: '%' }, { id: 'c_stred', label: 'Screen time reduction', dflt: 70, unit: '%' }, { id: 'c_sanred', label: 'Sanctions reduction', dflt: 55, unit: '%' }, { id: 'c_wcrel', label: 'WC release', dflt: 15, unit: '%' }, { id: 'c_wred', label: 'Waste reduction', dflt: 30, unit: '%' }],
    inputs: [
      { id: 'suppliers', label: 'Suppliers', min: 50, max: 50000, step: 50, dflt: 2000, unit: '', sub: '_shared' },
      { id: 'procurement', label: 'Procurement', min: 10, max: 50000, step: 10, dflt: 500, unit: 'M', mult: 1e6, fmtType: 'cur', sub: '_shared' },
      { id: 'disruption_cost', label: 'Disruption cost', min: 0, max: 500, step: 0.05, dflt: 15, unit: 'M', mult: 1e6, fmtType: 'cur', sub: 'disruption' },
      { id: 'screen_cost', label: 'Screening cost/yr', min: 50, max: 10000, step: 50, dflt: 1500, unit: 'K', mult: 1e3, fmtType: 'cur', sub: 'vendor' },
      { id: 'screen_time', label: 'Assessment days', min: 1, max: 30, step: 1, dflt: 14, unit: ' days', sub: 'vendor' },
      { id: 'sanction_risk', label: 'Sanctions risk', min: 0, max: 200, step: 1, dflt: 10, unit: 'M', mult: 1e6, fmtType: 'cur', sub: 'compliance' },
      { id: 'wc_tied', label: 'WC in inventory', min: 10, max: 10000, step: 10, dflt: 200, unit: 'M', mult: 1e6, fmtType: 'cur', sub: 'workcap' },
      { id: 'wc_rate', label: 'Cost of capital', min: 3, max: 15, step: 1, dflt: 8, unit: '%', sub: 'workcap' },
      { id: 'inv_waste', label: 'Inventory waste/yr', min: 1, max: 100, step: 1, dflt: 5, unit: 'M', mult: 1e6, fmtType: 'cur', sub: 'workcap' },
      { id: 'license', label: 'License', min: 100, max: 5000, step: 50, dflt: 600, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_ctx' },
      { id: 'impl', label: 'Implementation', min: 50, max: 3000, step: 50, dflt: 300, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_ctx' },
    ],
    calc: (v, subs, c) => {
      const rows = []; const bars = [];
      if (subs.disruption) { const d = v.disruption_cost * (c.c_dred / 100) + v.procurement * (c.c_pbuf / 100) * (c.c_dred / 100); rows.push({ n: 'Disruption', s: `${c.c_dred}%`, curr: v.disruption_cost, after: v.disruption_cost * (1 - c.c_dred / 100), sav: d }); }
      if (subs.vendor) { const d = v.screen_cost * (c.c_sred / 100), ds = v.screen_time * (c.c_stred / 100), dc = v.suppliers > 0 ? v.screen_cost / (v.suppliers * v.screen_time) : 0, ts = v.suppliers * dc * ds * 0.1; rows.push({ n: 'Screening', s: `${c.c_sred}%+time`, curr: v.screen_cost, after: v.screen_cost * (1 - c.c_sred / 100), sav: d + ts }); bars.push({ l: 'Assess', b: v.screen_time, a: Math.round(v.screen_time * (1 - c.c_stred / 100)), u: 'd', c: '#2563eb', mx: 30 }); }
      if (subs.compliance) { const rf = Math.min(v.suppliers / 2000, 2.5), ar = v.sanction_risk * Math.max(rf, 0.5); rows.push({ n: 'Sanctions', s: `${c.c_sanred}%`, curr: ar, after: ar * (1 - c.c_sanred / 100), sav: ar * (c.c_sanred / 100) }); }
      if (subs.workcap) { const w = v.wc_tied * (c.c_wcrel / 100) * (v.wc_rate / 100), i = v.inv_waste * (c.c_wred / 100); rows.push({ n: 'WC carrying', s: `${c.c_wcrel}%×${v.wc_rate}%`, curr: v.wc_tied * (v.wc_rate / 100), after: v.wc_tied * (1 - c.c_wcrel / 100) * (v.wc_rate / 100), sav: w }); rows.push({ n: 'Waste', s: `${c.c_wred}%`, curr: v.inv_waste, after: v.inv_waste * (1 - c.c_wred / 100), sav: i }); }
      const gross = rows.reduce((s, r) => s + r.sav, 0);
      return { rows, bars, gross, y1cost: v.license + v.impl, assume: `Disruption ${c.c_dred}%, Screen ${c.c_sred}%, Sanctions ${c.c_sanred}%, WC ${c.c_wcrel}%. Gartner 2024.` };
    }
  },
  {
    id: 'greenwash', name: 'Greenwashing', icon: '🌱', color: '#16A34A', desc: '6-Layer Forensic Loan Assessment',
    subs: [{ id: 'detection', label: 'Detection', on: true }, { id: 'subsidy', label: 'Subsidy Protection', on: true }, { id: 'tracking', label: 'Post-disbursement', on: true }],
    constants: [{ id: 'c_det', label: 'Detection rate', dflt: 70, unit: '%' }, { id: 'c_dmult', label: 'Default multiplier', dflt: 2, unit: '×' }, { id: 'c_pimp', label: 'Portfolio improve', dflt: 35, unit: '%' }, { id: 'c_sprev', label: 'Subsidy prevention', dflt: 65, unit: '%' }, { id: 'c_ared', label: 'Assessment reduction', dflt: 50, unit: '%' }, { id: 'c_mred', label: 'Monitor reduction', dflt: 60, unit: '%' }],
    inputs: [
      { id: 'portfolio', label: 'Loan portfolio', min: 50, max: 50000, step: 50, dflt: 1000, unit: 'M', mult: 1e6, fmtType: 'cur', sub: '_shared' },
      { id: 'applications', label: 'Applications/yr', min: 100, max: 50000, step: 100, dflt: 5000, unit: '', sub: '_shared' },
      { id: 'avg_loan', label: 'Avg loan size', min: 100, max: 50000, step: 100, dflt: 2000, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_shared' },
      { id: 'default_rate', label: 'Default rate', min: 1, max: 20, step: 0.5, dflt: 5, unit: '%', sub: 'detection' },
      { id: 'misrep_rate', label: 'Misrep rate', min: 2, max: 40, step: 1, dflt: 15, unit: '%', sub: 'detection' },
      { id: 'subsidy_pct', label: 'Subsidy rate', min: 0.5, max: 10, step: 0.5, dflt: 3, unit: '%', sub: 'subsidy' },
      { id: 'assess_cost', label: 'Assess cost/app', min: 500, max: 10000, step: 100, dflt: 2500, unit: '', fmtType: 'cur_raw', sub: '_shared' },
      { id: 'fte_cost', label: 'FTE cost', min: 10, max: 250, step: 5, dflt: 85, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_shared' },
      { id: 'license', label: 'License', min: 100, max: 5000, step: 50, dflt: 500, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_ctx' },
      { id: 'impl', label: 'Implementation', min: 50, max: 3000, step: 50, dflt: 250, unit: 'K', mult: 1e3, fmtType: 'cur', sub: '_ctx' },
    ],
    calc: (v, subs, c) => {
      const rows = []; const bars = [];
      const disb = v.applications * v.avg_loan, misrep = disb * (v.misrep_rate / 100);
      if (subs.detection) { const ml = misrep * (v.default_rate / 100) * c.c_dmult, s = ml * (c.c_det / 100); const ps = v.portfolio * (v.default_rate / 100) * (c.c_pimp / 100); rows.push({ n: 'Detection', s: `${c.c_det}%`, curr: ml, after: ml * (1 - c.c_det / 100), sav: s }); rows.push({ n: 'Portfolio', s: `${c.c_pimp}%`, curr: v.portfolio * (v.default_rate / 100), after: v.portfolio * (v.default_rate / 100) * (1 - c.c_pimp / 100), sav: ps }); bars.push({ l: 'Detect', b: 10, a: c.c_det, u: '%', c: '#059669', mx: 100 }); }
      if (subs.subsidy) { const lk = misrep * (v.subsidy_pct / 100), s = lk * (c.c_sprev / 100); rows.push({ n: 'Subsidy', s: `${c.c_sprev}%`, curr: lk, after: lk * (1 - c.c_sprev / 100), sav: s }); }
      if (subs.tracking) { const ta = v.applications * v.assess_cost, as = ta * (c.c_ared / 100); rows.push({ n: 'Assessment', s: `${c.c_ared}%`, curr: ta, after: ta * (1 - c.c_ared / 100), sav: as }); }
      const gross = rows.reduce((s, r) => s + r.sav, 0);
      return { rows, bars, gross, y1cost: v.license + v.impl, assume: `Detection ${c.c_det}%, Portfolio ${c.c_pimp}%, Subsidy ${c.c_sprev}%, Assessment ${c.c_ared}%. EU Taxonomy.` };
    }
  }
];

function ROICalculator() {
  const [activeTab, setActiveTab] = useState(0);
  const [cur, setCur] = useState('USD');
  const [rates, setRates] = useState({ ...FX_FALLBACK });
  const [values, setValues] = useState(() => {
    const v = {};
    TABS.forEach(t => { v[t.id] = {}; t.inputs.forEach(inp => { v[t.id][inp.id] = inp.dflt * (inp.mult || 1); }); });
    return v;
  });
  const [subs, setSubs] = useState(() => {
    const s = {};
    TABS.forEach(t => { s[t.id] = {}; if (t.subs) t.subs.forEach(sub => { s[t.id][sub.id] = sub.on; }); });
    return s;
  });
  const [consts, setConsts] = useState(() => {
    const c = {};
    TABS.forEach(t => { c[t.id] = {}; if (t.constants) t.constants.forEach(con => { c[t.id][con.id] = con.dflt; }); });
    return c;
  });

  useEffect(() => {
    fetch('https://api.frankfurter.dev/v1/latest?base=USD&symbols=SGD,EUR,GBP,INR,PHP,IDR')
      .then(r => r.json()).then(d => { if (d.rates) setRates(prev => ({ ...prev, ...d.rates })); }).catch(() => {});
  }, []);

  const f = useCallback((usd) => fmt(usd, cur, rates), [cur, rates]);

  const setSlider = (tabId, inputId, rawVal, mult) => {
    setValues(prev => ({ ...prev, [tabId]: { ...prev[tabId], [inputId]: rawVal * (mult || 1) } }));
  };

  const toggleSub = (tabId, subId) => {
    setSubs(prev => ({ ...prev, [tabId]: { ...prev[tabId], [subId]: !prev[tabId][subId] } }));
  };

  const setConst = (tabId, conId, val) => {
    setConsts(prev => ({ ...prev, [tabId]: { ...prev[tabId], [conId]: parseFloat(val) || 0 } }));
  };

  const resetTab = (tabId) => {
    const t = TABS.find(x => x.id === tabId);
    const v = {}; t.inputs.forEach(inp => { v[inp.id] = inp.dflt * (inp.mult || 1); });
    setValues(prev => ({ ...prev, [tabId]: v }));
    const c = {}; if (t.constants) t.constants.forEach(con => { c[con.id] = con.dflt; });
    setConsts(prev => ({ ...prev, [tabId]: c }));
    const s = {}; if (t.subs) t.subs.forEach(sub => { s[sub.id] = sub.on; });
    setSubs(prev => ({ ...prev, [tabId]: s }));
  };

  const renderTab = (t) => {
    const v = values[t.id] || {};
    const subState = subs[t.id] || {};
    const c = consts[t.id] || {};
    const R = t.calc(v, subState, c);
    const g = R.gross, cost = R.y1cost, net = g - cost;
    const roi = cost > 0 ? ((g / cost - 1) * 100) : 0;
    const pb = g > 0 ? Math.ceil(cost / (g / 12)) : 99;
    let cfs = [-(v.impl || 0)]; for (let y = 1; y <= 5; y++) cfs.push(g * Math.pow(1.08, y - 1) - (v.license || 0));
    const npv = npvCalc(cfs.slice(1), 0.08) - (v.impl || 0);
    const irr = irrCalc(cfs);

    const grouped = {};
    t.inputs.forEach(inp => { const g = inp.sub || '_shared'; if (!grouped[g]) grouped[g] = []; grouped[g].push(inp); });

    return (
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        {/* LEFT: Inputs */}
        <div style={{ width: 320, flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700 }}>Inputs</span>
            <button onClick={() => resetTab(t.id)} style={{ fontSize: 9, padding: '2px 8px', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>Reset</button>
          </div>
          {/* Sub toggles */}
          {t.subs && (
            <div style={{ marginBottom: 8, padding: 6, background: '#f9fafb', borderRadius: 4, border: '1px solid #f0f2f5' }}>
              <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>Enable/Disable</div>
              {t.subs.map(sub => (
                <label key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer', marginBottom: 2 }}>
                  <input type="checkbox" checked={subState[sub.id] || false} onChange={() => toggleSub(t.id, sub.id)} />
                  {sub.label}
                </label>
              ))}
            </div>
          )}
          {/* Constants */}
          {t.constants && t.constants.length > 0 && (
            <div style={{ marginBottom: 8, padding: 6, background: '#f0fdf4', borderRadius: 4, border: '1px solid #bbf7d0', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <span style={{ fontSize: 9, color: '#166534', fontWeight: 700, width: '100%' }}>ContexQ targets:</span>
              {t.constants.map(con => (
                <span key={con.id} style={{ fontSize: 9, display: 'inline-flex', alignItems: 'center', gap: 2, background: '#fff', padding: '2px 6px', borderRadius: 3, border: '1px solid #e5e7eb' }}>
                  {con.label} <input type="number" value={c[con.id] || con.dflt} onChange={e => setConst(t.id, con.id, e.target.value)} style={{ width: 36, border: 'none', fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: '#2563eb', textAlign: 'center', background: 'transparent' }} />{con.unit}
                </span>
              ))}
            </div>
          )}
          {/* Slider groups */}
          {t.subs && t.subs.map(sub => grouped[sub.id] && (
            <div key={sub.id} style={{ marginBottom: 8, opacity: subState[sub.id] ? 1 : 0.3, pointerEvents: subState[sub.id] ? 'auto' : 'none' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.color, marginBottom: 4 }}>{sub.label}</div>
              {grouped[sub.id].map(inp => renderSlider(t, inp, v))}
            </div>
          ))}
          {grouped._shared && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>Organization</div>
              {grouped._shared.map(inp => renderSlider(t, inp, v))}
            </div>
          )}
          {grouped._ctx && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#0d9488', marginBottom: 4 }}>ContexQ</div>
              {grouped._ctx.map(inp => renderSlider(t, inp, v))}
            </div>
          )}
        </div>

        {/* RIGHT: Results */}
        <div style={{ flex: 1, minWidth: 400 }}>
          {/* Hero stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
            {[
              { l: 'Net Y1', v: f(net), c: '#059669', s: f(cost) + ' invested' },
              { l: 'ROI', v: pct(roi), c: '#2563eb', s: f(g) + ' gross' },
              { l: '5yr NPV', v: f(npv), c: '#7c3aed', s: '@8%' + (irr ? ' · IRR ' + pct(irr * 100) : '') },
              { l: 'Payback', v: pb > 36 ? '36+mo' : pb + 'mo', c: '#d97706', s: pb <= 12 ? 'Y1' : 'Y' + Math.ceil(pb / 12) },
            ].map(h => (
              <div key={h.l} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', borderTop: `3px solid ${h.c}`, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
                <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>{h.l}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: h.c, fontFamily: 'monospace' }}>{h.v}</div>
                <div style={{ fontSize: 9, color: '#6b7280' }}>{h.s}</div>
              </div>
            ))}
          </div>

          {/* Exec summary */}
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 11 }}>
            <strong>Y1:</strong> {f(net)} net — <strong>{pct(roi)} ROI</strong>, <strong>{pb > 36 ? '36+' : pb}mo payback</strong>. <strong>5yr NPV:</strong> {f(npv)}{irr ? ` (IRR ${pct(irr * 100)})` : ''}.
          </div>

          {/* Savings table */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#059669', marginBottom: 8 }}>Savings Breakdown — Year 1</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead><tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: '#6b7280' }}>Category</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#6b7280' }}>Current</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#6b7280' }}>With ContexQ</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#6b7280' }}>Savings</th>
              </tr></thead>
              <tbody>
                {R.rows.map(r => (
                  <tr key={r.n} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '6px 8px' }}><div style={{ fontWeight: 600 }}>{r.n}</div><div style={{ fontSize: 9, color: '#9ca3af' }}>{r.s}</div></td>
                    <td style={{ textAlign: 'right', padding: '6px 8px', color: '#dc2626' }}>{r.curr > 0 ? f(r.curr) : '—'}</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px', color: '#2563eb' }}>{f(r.after)}</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px', color: '#059669', fontWeight: 700 }}>{f(r.sav)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #059669', background: '#f0fdf4' }}>
                  <td style={{ padding: '6px 8px', fontWeight: 800, color: '#059669' }}>Gross Savings</td><td></td><td></td>
                  <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 800, color: '#059669', fontSize: 13 }}>{f(g)}</td>
                </tr>
                <tr><td style={{ padding: '6px 8px', fontWeight: 600, color: '#dc2626' }}>Less: Y1 Cost</td><td></td><td></td>
                  <td style={{ textAlign: 'right', padding: '6px 8px', color: '#dc2626' }}>-{f(cost)}</td>
                </tr>
                <tr style={{ borderTop: '2px solid #e5e7eb' }}>
                  <td style={{ padding: '6px 8px', fontWeight: 800, color: net >= 0 ? '#059669' : '#dc2626' }}>Net Y1</td><td></td><td></td>
                  <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 800, color: net >= 0 ? '#059669' : '#dc2626', fontSize: 13 }}>{f(net)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Before/After bars */}
          {R.bars && R.bars.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', marginBottom: 8 }}>Before vs After</div>
              {R.bars.map(b => (
                <div key={b.l} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 2 }}>
                    <span style={{ fontWeight: 600 }}>{b.l}</span>
                    <span style={{ color: b.c, fontWeight: 700 }}>{b.a}{b.u}</span>
                  </div>
                  <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, position: 'relative' }}>
                    <div style={{ position: 'absolute', height: '100%', borderRadius: 4, width: `${(b.b / b.mx) * 100}%`, background: b.c, opacity: 0.2 }} />
                    <div style={{ position: 'absolute', height: '100%', borderRadius: 4, width: `${(b.a / b.mx) * 100}%`, background: b.c }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 5yr Projection */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>5-Year Projection</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead><tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280' }}>Year</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b7280' }}>Gross</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b7280' }}>Cost</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b7280' }}>Net</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b7280' }}>Cumulative</th>
              </tr></thead>
              <tbody>{(() => {
                let cum = 0;
                return [1, 2, 3, 4, 5].map(y => {
                  const gs = g * Math.pow(1.08, y - 1), co = y === 1 ? cost : (v.license || 0), n = gs - co;
                  cum += n;
                  return (
                    <tr key={y} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>Y{y}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', color: '#059669' }}>{f(gs)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', color: '#dc2626' }}>-{f(co)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', color: n >= 0 ? '#059669' : '#dc2626' }}>{f(n)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', color: cum >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{f(cum)}</td>
                    </tr>
                  );
                });
              })()}</tbody>
            </table>
          </div>

          {/* Assumptions */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', marginBottom: 4 }}>Assumptions</div>
            <div style={{ fontSize: 10, color: '#6b7280' }}>{R.assume}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderSlider = (t, inp, v) => {
    const raw = (v[inp.id] || 0) / (inp.mult || 1);
    // Show enough decimals to actually see a step change (fmt() truncates
    // to 1 decimal at the M/K threshold, which hides sub-100K steps).
    const stepDecimals = (String(inp.step).split('.')[1] || '').length;
    const preciseCur = (val) => {
      const abs = Math.abs(val);
      const s = CS[cur] || '$';
      const n = val * (rates[cur] || 1);
      if (abs >= 1e6) return s + (n / 1e6).toFixed(Math.max(1, stepDecimals)).replace(/\.?0+$/, '') + 'M';
      if (abs >= 1e3) return s + (n / 1e3).toFixed(0) + 'K';
      return s + Math.round(n).toLocaleString();
    };
    let display = '';
    if (inp.fmtType === 'cur') display = preciseCur(raw * (inp.mult || 1));
    else if (inp.fmtType === 'cur_raw') display = preciseCur(raw);
    else if (inp.fmtType === 'cnt') display = fN(raw * (inp.mult || 1));
    else display = raw + (inp.unit || '');

    // Nudge by exactly one step (+ or −), clamp to [min, max].
    // Float-safe rounding based on the step's decimal precision.
    const bump = (dir) => {
      const decimals = (String(inp.step).split('.')[1] || '').length;
      const factor = Math.pow(10, decimals);
      const next = Math.max(inp.min, Math.min(inp.max, Math.round((raw + dir * inp.step) * factor) / factor));
      setSlider(t.id, inp.id, next, inp.mult);
    };
    const stepBtn = (disabled) => ({
      width: 18, height: 18, border: '1px solid #e5e7eb', borderRadius: 3,
      background: '#fff', color: disabled ? '#cbd5e1' : '#6b7280',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 700, padding: 0, lineHeight: 1,
      userSelect: 'none',
    });
    const atMin = raw <= inp.min;
    const atMax = raw >= inp.max;

    return (
      <div key={inp.id} style={{ marginBottom: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10 }}>
          <span style={{ color: '#6b7280' }}>{inp.label}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <button type="button" onClick={() => bump(-1)} disabled={atMin}
              title={`−1 step (${inp.step}${inp.unit || ''})`} style={stepBtn(atMin)}>−</button>
            <span style={{ fontWeight: 700, fontFamily: 'monospace', color: '#111', minWidth: 56, textAlign: 'right' }}>{display}</span>
            <button type="button" onClick={() => bump(1)} disabled={atMax}
              title={`+1 step (${inp.step}${inp.unit || ''})`} style={stepBtn(atMax)}>+</button>
          </span>
        </div>
        <input type="range" min={inp.min} max={inp.max} step={inp.step} value={raw}
          onChange={e => setSlider(t.id, inp.id, parseFloat(e.target.value), inp.mult)}
          style={{ width: '100%', height: 4, cursor: 'pointer' }} />
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0d9488' }}>Enterprise ROI Calculator</h2>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>Estimate savings across 5 use-case domains</div>
        </div>
        <select value={cur} onChange={e => setCur(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
          {Object.keys(CS).map(c => <option key={c} value={c}>{CS[c]} {c}</option>)}
        </select>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 1, marginBottom: 16, overflowX: 'auto' }}>
        {TABS.map((t, i) => (
          <button key={t.id} onClick={() => setActiveTab(i)} style={{
            padding: '8px 14px', background: activeTab === i ? '#fff' : '#f9fafb', border: 'none',
            borderBottom: activeTab === i ? `2px solid ${t.color}` : '2px solid transparent',
            color: activeTab === i ? t.color : '#9ca3af', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
          }}>
            {t.icon} {t.name}
          </button>
        ))}
      </div>

      {/* Active tab */}
      {renderTab(TABS[activeTab])}
    </div>
  );
}

export default ROICalculator;
